import { User } from '@run-diffusion/shared';

/**
 * The Boards v2 tour (IL-095): a hands-on walk through a practice board (a
 * session added, Tab to write the prompt, a run, a picture placed, Studio
 * view, an edit), played by the board bundle's mountOnboarding at /welcome.
 *
 * Who sees it, and when:
 *
 * - A new account, straight after the welcome survey, when RunDiffusion has
 *   switched new accounts into Boards v2 (masterConfigs/boardsV2.newAccounts,
 *   on the Boards v2 Rollout admin page). It's required: the app sends them
 *   back to it until it's done, and it has no Skip. It ends by making their
 *   first board, and the account has Boards v2 from then on.
 * - Anyone else (a team's member, an invited person, an enterprise user) the
 *   first time they make a Boards v2 board, if they've never seen the tour:
 *   it plays before the board opens, and ends by opening it. Enterprise
 *   accounts and RunDiffusion staff can skip it.
 * - Anyone, again, from a board's menu or the boards list's hero.
 *
 * Its state rides on the user doc with the app's other tours
 * (featureToursCompletedMap), so it follows the person to every browser.
 */
export const BOARDS_TOUR_ID = 'boards-v2-onboarding';
/** Raise when the tour changes enough that everyone should see it again (it won't auto-start for anyone already past it). */
export const BOARDS_TOUR_VERSION = 1;

export type BoardsTourStatus = 'due' | 'started' | 'completed' | 'dismissed';

export interface BoardsTourState {
	status: BoardsTourStatus;
	version: number;
	/** a new account's tour: the app sends them to it until it's done */
	required?: boolean;
	/** the account began in Boards v2 (a new account, after the survey): it has Boards v2 on its own */
	newAccount?: boolean;
	updatedAt?: unknown;
}

type TourUser = Pick<User, 'featureToursCompletedMap'> | null | undefined;

export function boardsTourState (user: TourUser): BoardsTourState | null {
	const map = user && user.featureToursCompletedMap;
	const entry = map ? (map as Record<string, unknown>)[BOARDS_TOUR_ID] : null;
	if (!entry || typeof entry !== 'object') return null;
	return entry as BoardsTourState;
}

/** A new account that hasn't finished its tour: the app keeps sending it there. */
export function boardsTourRequired (user: TourUser): boolean {
	const s = boardsTourState(user);
	return !!s && !!s.required && (s.status === 'due' || s.status === 'started');
}

/** The account began in Boards v2 (a new account sent through the tour): it has Boards v2 without a team. */
export function startedInBoardsV2 (user: TourUser): boolean {
	const s = boardsTourState(user);
	return !!s && !!s.newAccount;
}

/** Ever shown the tour (started it, finished it, skipped it), or owed it: it won't start on its own again. */
export function boardsTourSeen (user: TourUser): boolean {
	return !!boardsTourState(user);
}

/**
 * The state a tour's progress writes: started, completed, or skipped
 * ('dismissed'). What's already settled stays settled: a replay doesn't
 * undo a finished tour, and the new-account flags are kept.
 */
export function nextBoardsTourState (prev: BoardsTourState | null, progress: 'due' | 'started' | 'completed' | 'skipped', flags: { required?: boolean, newAccount?: boolean } = {}): BoardsTourState {
	const keep = {
		...(prev && prev.required ? { required: true } : {}),
		...(prev && prev.newAccount ? { newAccount: true } : {}),
		...(flags.required ? { required: true } : {}),
		...(flags.newAccount ? { newAccount: true } : {}),
	};
	const was: BoardsTourStatus | null = prev ? prev.status : null;
	let status: BoardsTourStatus;
	if (progress === 'completed') status = 'completed';
	else if (was === 'completed') status = 'completed';
	else if (progress === 'skipped') status = 'dismissed';
	else if (progress === 'due') status = was === 'started' ? 'started' : 'due';
	else status = was === 'dismissed' ? 'dismissed' : 'started';
	return { status, version: BOARDS_TOUR_VERSION, ...keep };
}

/**
 * Skip is for enterprise accounts (by plan, by a verified domain claim, or by
 * the fallback domain list) and RunDiffusion staff. A new account's tour has
 * none: it's the way in. Taking it again can always be left (the tour's Exit).
 */
export function canSkipBoardsTour (who: {
	user: Pick<User, 'isAdmin' | 'email'> | null | undefined,
	enterprise: boolean,
	required: boolean,
}): boolean {
	const email = ((who.user && who.user.email) || '').trim().toLowerCase();
	const staff = !!(who.user && (who.user.isAdmin || email.endsWith('@rundiffusion.com')));
	if (staff) return true;
	if (who.required) return false;
	return who.enterprise;
}

/**
 * A board just made from the boards list gets the tour first when it's the
 * person's first Boards v2 board and they've never seen the tour.
 * `boardsOwned` counts their canvas boards in this account, the new one
 * included.
 */
export function tourBeforeFirstBoard (user: TourUser, boardsOwned: number): boolean {
	return !boardsTourSeen(user) && boardsOwned <= 1;
}

const SNOOZE_KEY = 'rd-boards-tour-later';

/** The tour couldn't load: this browser stops sending a new account to it until the tab closes. */
export function snoozeBoardsTour (): void {
	try {
		window.sessionStorage.setItem(SNOOZE_KEY, '1');
	} catch (e) {
		// private mode: they're sent to it again, where Continue for now is offered again
	}
}

export function boardsTourSnoozed (): boolean {
	try {
		return window.sessionStorage.getItem(SNOOZE_KEY) === '1';
	} catch (e) {
		return false;
	}
}

/** Where /welcome comes back to, kept to this app's own paths. */
export function safeReturnPath (path: unknown): string | null {
	if (typeof path !== 'string') return null;
	return /^\/(?!\/)[^\s\\]*$/.test(path) ? path : null;
}
